// Listings-tab state + handlers, extracted from market-ui.ts to keep
// that file under the §10 400-line cap as additional tabs land.
//
// Owns: selected inventory slot, listing-form price, list-pending
// flag, per-row pending Buy/Cancel set. Construction takes a Host
// shape so this class stays decoupled from MarketUI internals; the
// host wires sendFn (network), setSelectedSlotInUI (inventory
// highlight), and onChange (rebuildBody trigger).
//
// Symmetry note: NPC tab state lives on MarketNpcTab itself (no
// inventory selection coupling). Listings tab needs the centralized
// host plumbing because it interacts with InventoryUI selection
// mode + needs the rebuildBody hook to refresh on every state change.

import { balance } from "./types.ts";
import type { ItemId } from "../../shared/items.ts";
import type { ClientMessage, InventorySlot } from "../../shared/messages.ts";
import {
  buildBuyListingMessage,
  buildCancelListingMessage,
  buildListItemMessage,
} from "../../shared/messages.ts";
import { getNpcItem } from "../../shared/npc-items.ts";

export type ListingsStateHost = {
  readonly sendFn: (msg: ClientMessage) => void;
  setSelectedSlotInUI(slotIndex: number | null): void;
  onChange(): void;
};

export class MarketListingsState {
  selectedSlotIndex: number | null = null;
  selectedSlot: InventorySlot | null = null;
  listPrice: number = balance.economy.minListingPrice;
  listPending = false;
  pendingListingIds = new Set<string>();

  constructor(private readonly host: ListingsStateHost) {}

  reset(): void {
    this.selectedSlotIndex = null;
    this.selectedSlot = null;
    this.listPrice = balance.economy.minListingPrice;
    this.listPending = false;
    this.pendingListingIds.clear();
  }

  handleInventorySelect(slotIndex: number, slot: InventorySlot): void {
    if (this.selectedSlotIndex === slotIndex) {
      this.selectedSlotIndex = null;
      this.selectedSlot = null;
    } else {
      this.selectedSlotIndex = slotIndex;
      this.selectedSlot = slot;
      // Reset price floor on selection change so the spinner starts
      // at a valid value regardless of the previous item.
      this.listPrice = balance.economy.minListingPrice;
    }
    this.host.setSelectedSlotInUI(this.selectedSlotIndex);
    this.host.onChange();
  }

  handlePriceDelta(delta: number): void {
    const { min, max } = this.priceBounds();
    const next = Math.max(min, Math.min(max, this.listPrice + delta));
    if (next === this.listPrice) return;
    this.listPrice = next;
    this.host.onChange();
  }

  // NPC-tradable items clamp at maxListingPriceMultiplier × NPC
  // reference sell price; crafted items (no NPC entry) use
  // maxCredits. Mirrors the server-side validation; per DECISIONS
  // 2026-04-18 "Phase 4 Step 9 max-price multiplier applies to
  // NPC-tradable items only".
  priceBounds(): { min: number; max: number } {
    const min = balance.economy.minListingPrice;
    if (!this.selectedSlot) return { min, max: balance.economy.maxCredits };
    const cfg = getNpcItem(this.selectedSlot.itemId);
    if (!cfg) return { min, max: balance.economy.maxCredits };
    const reference = Math.round(cfg.basePrice * (1 + balance.npc.baseMarginRatio));
    return { min, max: reference * balance.economy.maxListingPriceMultiplier };
  }

  handleBuy(listingId: string): void {
    if (this.pendingListingIds.has(listingId)) return;
    this.pendingListingIds.add(listingId);
    this.host.sendFn(buildBuyListingMessage(listingId));
    this.host.onChange();
  }

  handleCancel(listingId: string): void {
    if (this.pendingListingIds.has(listingId)) return;
    this.pendingListingIds.add(listingId);
    this.host.sendFn(buildCancelListingMessage(listingId));
    this.host.onChange();
  }

  handleList(itemId: ItemId, price: number): void {
    if (this.listPending) return;
    this.listPending = true;
    // Phase 4 quantity always 1; multi-quantity listings are Phase 5+.
    this.host.sendFn(buildListItemMessage(itemId, price, 1));
    this.host.onChange();
  }

  clearListPending(): boolean {
    if (!this.listPending) return false;
    this.listPending = false;
    return true;
  }

  clearListingPending(listingId: string): boolean {
    return this.pendingListingIds.delete(listingId);
  }
}
